import { AlertCircle, AlertTriangle, CheckCircle } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useFormatting } from "@/hooks/useFormatting";
import {
	applyScenarioToForecast,
	formatCoverageValue,
} from "@/lib/scenarioEngine";
import {
	getCashDurationShortTerm,
	getForecastTerm,
	getSystemTerminology,
} from "@/lib/terminology-config";
import type { ScenarioChange } from "@/types/scenarios";
import { getCurrencySymbol } from "@/utils/currency";

interface ScenarioImpactPreviewProps {
	// All adjustments from all groups (builder internal format — used for confidence score)
	adjustments: {
		revenue: any[];
		cost: any[];
	};
	// Live preview data (from forms being edited)
	livePreview?: {
		revenue: any | null;
		cost: any | null;
	};
	// Actual ScenarioChange[] from the scenario — used by the engine for accurate metrics
	scenarioChanges?: ScenarioChange[];
	// Base forecast data (from current state) — kept for fallback
	baseForecast?: {
		currentCash: number;
		runway: number;
		minCash: number;
		minCashDate: string;
	};
	// Render mode: 'chart-only' renders just the chart; 'sidebar' renders risk/confidence/summary only; default renders all
	mode?: "chart-only" | "sidebar" | "full";
}

export const ScenarioImpactPreview: React.FC<ScenarioImpactPreviewProps> = ({
	adjustments,
	livePreview = {
		revenue: null,
		cost: null,
	},
	scenarioChanges,
	baseForecast: baseForecastProp,
	mode = "full",
}) => {
	const { formatDate, formatCurrency } = useFormatting();

	// ── Engine-driven calculations ──────────────────────────────────────────────
	// When scenarioChanges are provided, use the real engine for accurate metrics.
	// Falls back to the simplified burn rate formula if not provided (e.g. new scenario).
	const {
		baseForecast: baseForecastData,
		isLoading: baseForecastLoading,
		forecastNotReady,
	} = useBaseForecast();

	// Derive base forecast metrics from context data when prop is not explicitly provided
	const derivedBaseForecast = useMemo((): {
		currentCash: number;
		runway: number;
		minCash: number;
		minCashDate: string;
	} | null => {
		if (baseForecastData.length === 0) return null;
		const first = baseForecastData[0];
		let minBal = first?.balance ?? 0;
		let minDate: Date | string = first?.date ?? new Date();
		let totalIn = 0;
		let totalOut = 0;
		for (const p of baseForecastData) {
			if (p.balance < minBal) {
				minBal = p.balance;
				minDate = p.date;
			}
			totalIn += p.incoming;
			totalOut += p.outgoing;
		}
		const avgDailyBurn = (totalOut - totalIn) / baseForecastData.length;
		const runway =
			avgDailyBurn > 0 ? (first?.balance ?? 0) / avgDailyBurn / 30.44 : 18;
		return {
			currentCash: first?.balance ?? 0,
			runway: Math.round(runway * 10) / 10,
			minCash: Math.round(minBal),
			minCashDate:
				minDate instanceof Date
					? minDate.toISOString().split("T")[0]
					: String(minDate),
		};
	}, [baseForecastData]);

	// Use prop if explicitly passed, otherwise use derived values from context
	const baseForecast = baseForecastProp ?? derivedBaseForecast;

	const engineResult = useMemo(() => {
		if (!scenarioChanges || scenarioChanges.length === 0) return null;
		return applyScenarioToForecast(baseForecastData, scenarioChanges);
	}, [scenarioChanges, baseForecastData]);

	const engineMetrics = engineResult?.metrics ?? null;
	const engineForecast = engineResult?.projectedForecast ?? null;
	// Get terminology configuration
	const [metricLabel, setMetricLabel] = useState<string>("Runway");
	const [metricUnit, setMetricUnit] = useState<string>("months");

	useEffect(() => {
		try {
			const updateTerminology = () => {
				const terminology = getSystemTerminology();
				const cashDurationTerm = getCashDurationShortTerm();

				// Capitalize first letter
				const label =
					cashDurationTerm.charAt(0).toUpperCase() + cashDurationTerm.slice(1);
				setMetricLabel(label);

				// Determine unit based on terminology type
				if (
					terminology.cashDuration === "runway" ||
					terminology.cashDuration === "coverage" ||
					terminology.cashDuration === "reserve" ||
					terminology.cashDuration === "buffer"
				) {
					setMetricUnit("months");
				} else if (terminology.cashDuration === "date") {
					setMetricUnit("date");
				} else {
					setMetricUnit("months");
				}
			};

			updateTerminology();

			// Listen for terminology updates
			const handleTerminologyUpdate = () => updateTerminology();
			window.addEventListener("terminology-updated", handleTerminologyUpdate);

			return () => {
				window.removeEventListener(
					"terminology-updated",
					handleTerminologyUpdate,
				);
			};
		} catch (error) {
			console.error("[ScenarioImpactPreview] Error in useEffect:", error);
		}
	}, []);

	// Calculate total adjustments count
	const totalAdjustments = adjustments.revenue.length + adjustments.cost.length;

	// Calculate net impact from all adjustments + live preview
	const calculateNetImpact = () => {
		let netImpact = 0;

		// Revenue adjustments (positive impact)
		adjustments.revenue.forEach((adj) => {
			if (adj.type === "adjust-revenue-percent") {
				netImpact += adj.monthlyImpact || 0;
			} else if (adj.type === "change-revenue") {
				// Canonical type for adjust-revenue-percent
				netImpact += adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "add-scenario-transaction" ||
				adj.type === "add-revenue"
			) {
				// Form type or canonical type for adding a revenue transaction
				netImpact += adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-revenue"
			) {
				// Form type or canonical type for adjusting a specific transaction
				netImpact += (adj.valueChange ?? adj.details?.valueChange ?? 0) / 12;
			}
		});

		// Add live revenue preview (if form is being edited)
		if (livePreview.revenue) {
			const adj = livePreview.revenue;
			if (adj.type === "adjust-revenue-percent") {
				netImpact += adj.monthlyImpact || 0;
			} else if (adj.type === "change-revenue") {
				netImpact += adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "add-scenario-transaction" ||
				adj.type === "add-revenue"
			) {
				netImpact += adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-revenue"
			) {
				netImpact += (adj.valueChange ?? adj.details?.valueChange ?? 0) / 12;
			}
		}

		// Cost adjustments (savings = positive, increases = negative)
		adjustments.cost.forEach((adj) => {
			if (adj.type === "adjust-costs-percent") {
				netImpact += adj.monthlyImpact || 0; // Negative for increases, positive for reductions
			} else if (adj.type === "change-costs") {
				// Canonical type for adjust-costs-percent
				const monthlySavings = adj.details?.monthlySavings ?? 0;
				const monthlyIncrease = adj.details?.monthlyIncrease ?? 0;
				if (adj.details?.changeType === "reduction") {
					netImpact += Number(monthlySavings);
				} else {
					netImpact -= Number(monthlyIncrease);
				}
			} else if (
				adj.type === "add-scenario-transaction" ||
				adj.type === "add-cost"
			) {
				// Form type or canonical type for adding a cost transaction
				netImpact -= adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-cost"
			) {
				// Form type or canonical type for adjusting a specific cost transaction
				netImpact += (adj.valueChange ?? adj.details?.valueChange ?? 0) / 12;
			}
		});

		// Add live cost preview
		if (livePreview.cost) {
			const adj = livePreview.cost;
			if (adj.type === "adjust-costs-percent") {
				netImpact += adj.monthlyImpact || 0;
			} else if (adj.type === "change-costs") {
				const monthlySavings = adj.details?.monthlySavings ?? 0;
				const monthlyIncrease = adj.details?.monthlyIncrease ?? 0;
				if (adj.details?.changeType === "reduction") {
					netImpact += Number(monthlySavings);
				} else {
					netImpact -= Number(monthlyIncrease);
				}
			} else if (
				adj.type === "add-scenario-transaction" ||
				adj.type === "add-cost"
			) {
				netImpact -= adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-cost"
			) {
				netImpact += (adj.valueChange ?? adj.details?.valueChange ?? 0) / 12;
			}
		}

		return netImpact;
	};

	const netImpact = calculateNetImpact();
	const monthlyImpact = netImpact;

	// Helper function: Round to nearest £1,000
	const roundToThousand = (value: number): number => {
		return Math.round(value / 1000) * 1000;
	};

	// Helper function: Round to 1 decimal place
	const roundToOneDecimal = (value: number): number => {
		return Math.round(value * 10) / 10;
	};

	// ── Base forecast unavailable guard ──────────────────────────────────────────
	// When no base forecast data exists (live mode, no forecast run), render an
	// empty state instead of calculations that depend on base metrics.
	if (!baseForecast) {
		if (baseForecastLoading) {
			return (
				<Card>
					<CardContent className="py-8 text-center">
						<p className="text-sm text-muted-foreground">
							Loading base forecast…
						</p>
					</CardContent>
				</Card>
			);
		}
		return (
			<Card>
				<CardContent className="py-8 text-center">
					<p className="text-sm text-muted-foreground">
						{forecastNotReady
							? "Run a base forecast first to see impact analysis"
							: "Base forecast not available"}
					</p>
				</CardContent>
			</Card>
		);
	}

	// ── Engine-driven metrics (when scenarioChanges provided) ──────────────────
	// These replace the simplified burn rate formula for accuracy.
	// Falls back to simplified formula for new scenarios with no saved changes.

	const scenarioCash = engineMetrics
		? engineMetrics.endingCash
		: roundToThousand(baseForecast.currentCash + monthlyImpact * 3);

	const baseBurnRate = baseForecast.currentCash / baseForecast.runway;
	const newBurnRate = baseBurnRate - monthlyImpact;

	let scenarioRunway: number;
	let runwayChange: number;
	let isPositiveCashFlow = false;

	if (engineMetrics) {
		scenarioRunway = engineMetrics.runway;
		isPositiveCashFlow = !isFinite(scenarioRunway);
		runwayChange = isPositiveCashFlow
			? Infinity
			: roundToOneDecimal(scenarioRunway - baseForecast.runway);
	} else if (newBurnRate <= 0) {
		scenarioRunway = Infinity;
		isPositiveCashFlow = true;
		runwayChange = Infinity;
	} else {
		scenarioRunway = roundToOneDecimal(baseForecast.currentCash / newBurnRate);
		runwayChange = roundToOneDecimal(scenarioRunway - baseForecast.runway);
	}

	const scenarioMinCash = engineMetrics
		? engineMetrics.minCash
		: roundToThousand(Math.max(0, baseForecast.minCash + monthlyImpact * 2));

	const cashChange = roundToThousand(scenarioCash - baseForecast.currentCash);
	const minCashChange = roundToThousand(scenarioMinCash - baseForecast.minCash);

	// Risk assessment
	const getRiskLevel = () => {
		if (scenarioMinCash < 50000)
			return {
				level: "High",
				color: "text-red-600",
				bgColor: "bg-red-100",
				icon: AlertCircle,
			};
		if (scenarioMinCash < 100000)
			return {
				level: "Medium",
				color: "text-amber-600",
				bgColor: "bg-amber-100",
				icon: AlertTriangle,
			};
		return {
			level: "Low",
			color: "[color:var(--accent-success-500)]",
			bgColor: "bg-green-100",
			icon: CheckCircle,
		};
	};

	const risk = getRiskLevel();
	const RiskIcon = risk.icon;

	// Calculate confidence score
	const calculateConfidence = () => {
		if (totalAdjustments === 0) return 100;

		let totalWeight = 0;
		let weightedSum = 0;

		// Cost adjustments = 85% (likely)
		adjustments.cost.forEach((adj) => {
			if (adj.type === "adjust-costs-percent" || adj.type === "change-costs") {
				// Percentage adjustments are more certain
				weightedSum += 90;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-cost"
			) {
				// Transaction adjustments are very certain
				weightedSum += 95;
			} else {
				// Scenario transactions (add-scenario-transaction / add-cost) depend on confidence level
				const confidence =
					adj.confidence || adj.details?.confidence || "probable";
				if (confidence === "certain") weightedSum += 95;
				else if (confidence === "probable") weightedSum += 75;
				else if (confidence === "possible") weightedSum += 50;
				else weightedSum += 25; // unlikely
			}
			totalWeight += 1;
		});

		// Revenue adjustments = 70-90% (depends on type)
		adjustments.revenue.forEach((adj) => {
			if (
				adj.type === "adjust-revenue-percent" ||
				adj.type === "change-revenue"
			) {
				// Percentage adjustments are fairly certain
				weightedSum += 85;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-revenue"
			) {
				// Transaction adjustments are very certain
				weightedSum += 95;
			} else {
				// Scenario transactions (add-scenario-transaction / add-revenue) depend on confidence level
				const confidence =
					adj.confidence || adj.details?.confidence || "probable";
				if (confidence === "certain") weightedSum += 95;
				else if (confidence === "probable") weightedSum += 75;
				else if (confidence === "possible") weightedSum += 50;
				else weightedSum += 25; // unlikely
			}
			totalWeight += 1;
		});

		return totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 100;
	};

	const confidence = calculateConfidence();

	// Generate chart data (90 days)
	// Uses real engine projection when available, falls back to burn rate formula
	const chartData = useMemo(() => {
		if (engineForecast && engineForecast.length > 0) {
			// Sample every 7 days for chart readability
			return engineForecast
				.filter((_, i) => i % 7 === 0)
				.map((point) => ({
					date: formatDate(point.date, { day: "numeric", month: "short" }),
					base: Math.max(0, point.baseBalance),
					scenario: Math.max(0, point.scenarioBalance),
					threshold: 50000,
				}));
		}

		// Fallback: simplified linear projection
		const baseDailyBurn = baseBurnRate / 30;
		const newDailyBurn = newBurnRate / 30;
		const today = new Date();
		const data = [];
		for (let i = 0; i <= 90; i += 7) {
			const date = new Date(today);
			date.setDate(date.getDate() + i);
			data.push({
				date: formatDate(date, { day: "numeric", month: "short" }),
				base: Math.max(0, baseForecast.currentCash - baseDailyBurn * i),
				scenario: Math.max(0, baseForecast.currentCash - newDailyBurn * i),
				threshold: 50000,
			});
		}
		return data;
	}, [
		engineForecast,
		baseBurnRate,
		newBurnRate,
		baseForecast.currentCash,
		formatDate,
	]);

	// Chart card — rendered in chart-only and full mode
	const chartCard = (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-medium text-gray-600">
					90-Day {getForecastTerm(true)}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={220}>
					<AreaChart
						data={chartData}
						margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
					>
						<defs>
							<linearGradient id="colorScenario" x1="0" y1="0" x2="0" y2="1">
								<stop
									offset="5%"
									stopColor="var(--accent-sky-500)"
									stopOpacity={0.3}
								/>
								<stop
									offset="95%"
									stopColor="var(--accent-sky-500)"
									stopOpacity={0}
								/>
							</linearGradient>
						</defs>
						<CartesianGrid
							strokeDasharray="3 3"
							stroke="hsl(var(--chart-grid))"
						/>
						<XAxis
							dataKey="date"
							tick={{ fontSize: 10 }}
							stroke="hsl(var(--chart-gray))"
						/>
						<YAxis
							tick={{ fontSize: 10 }}
							stroke="hsl(var(--chart-gray))"
							tickFormatter={(value) =>
								`${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
							}
						/>
						<Tooltip
							formatter={(value) =>
								formatCurrency(typeof value === "number" ? value : 0)
							}
							contentStyle={{ fontSize: "12px", borderRadius: "6px" }}
						/>
						<Line
							type="monotone"
							dataKey="base"
							stroke="hsl(var(--chart-gray))"
							strokeWidth={2}
							strokeDasharray="5 5"
							dot={false}
							name={`Base ${getForecastTerm(true)}`}
						/>
						<Area
							type="monotone"
							dataKey="scenario"
							stroke="var(--accent-sky-500)"
							strokeWidth={2}
							fill="url(#colorScenario)"
							name="This Scenario"
						/>
						<Line
							type="monotone"
							dataKey="threshold"
							stroke="var(--mc-error-icon)"
							strokeWidth={1}
							strokeDasharray="3 3"
							dot={false}
							name="Min Threshold"
						/>
					</AreaChart>
				</ResponsiveContainer>
				<div className="flex items-center justify-center gap-4 mt-3 text-xs">
					<div className="flex items-center gap-1">
						<div
							className="w-3 h-0.5"
							style={{ borderTop: "2px dashed hsl(var(--chart-gray))" }}
						/>
						<span className="text-gray-600">Base {getForecastTerm(true)}</span>
					</div>
					<div className="flex items-center gap-1">
						<div
							className="w-3 h-0.5"
							style={{ backgroundColor: "var(--accent-sky-500)" }}
						/>
						<span className="text-gray-600">This Scenario</span>
					</div>
					<div className="flex items-center gap-1">
						<div
							className="w-3 h-0.5"
							style={{ borderTop: "1px dashed var(--mc-error-icon)" }}
						/>
						<span className="text-gray-600">Min Threshold</span>
					</div>
				</div>
			</CardContent>
		</Card>
	);

	// Chart-only mode — just the chart, no header or sidebar sections
	if (mode === "chart-only") {
		return chartCard;
	}

	return (
		<div
			className={
				mode === "sidebar"
					? "grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch"
					: "space-y-4"
			}
		>
			{/* Header — full mode only */}
			{mode === "full" && (
				<div className="flex items-center gap-2 mb-4">
					<div
						className="w-1 h-6 rounded-full"
						style={{ backgroundColor: "var(--accent-teal-500)" }}
					/>
					<h3 className="text-lg font-semibold text-gray-900">
						Impact Summary
					</h3>
				</div>
			)}

			{/* Chart — full mode only */}
			{mode === "full" && chartCard}

			{/* Risk, Confidence, Summary */}

			{/* Risk Assessment */}
			<Card>
				<CardContent className="pt-3 pb-3 px-4">
					<div className="space-y-1.5">
						<div className="flex items-center justify-between">
							<span className="text-xs text-gray-600">Risk Level</span>
							<div className="flex items-center gap-1">
								<RiskIcon className={`w-3.5 h-3.5 ${risk.color}`} />
								<span className={`text-xs font-semibold ${risk.color}`}>
									{risk.level}
								</span>
							</div>
						</div>
						<div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
							<span className="text-xs text-gray-600">Min Cash</span>
							<span className="text-xs font-semibold text-gray-900">
								{formatCurrency(scenarioMinCash, "full")}
							</span>
						</div>
						<div className="flex items-center justify-between">
							<span className="text-xs text-gray-600">vs Base</span>
							<span
								className={`text-xs font-semibold ${minCashChange >= 0 ? "[color:var(--accent-success-500)]" : "text-amber-600"}`}
							>
								{minCashChange >= 0 ? "+" : ""}
								{formatCurrency(minCashChange, "full")}
							</span>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Confidence Score */}
			<Card>
				<CardContent className="pt-3 pb-3 px-4 h-full flex flex-col justify-between">
					<div className="space-y-1.5">
						<div className="flex items-center justify-between">
							<span className="text-xs text-gray-600">Confidence</span>
							<span className="text-xs font-semibold text-gray-900">
								{confidence}%
							</span>
						</div>
						<div className="w-full bg-gray-200 rounded-full h-1 mb-1">
							<div
								className={`h-1 rounded-full ${confidence >= 80 ? "[background:var(--accent-success-500)]" : confidence >= 60 ? "bg-amber-500" : "bg-gray-400"}`}
								style={{ width: `${confidence}%` }}
							/>
						</div>
						<div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
							<span className="text-xs text-gray-600">Rating</span>
							<span className="text-xs font-semibold text-gray-900">
								{confidence >= 80
									? "High"
									: confidence >= 60
										? "Medium"
										: "Low"}
							</span>
						</div>
						<div className="flex items-center justify-between">
							<span className="text-xs text-gray-600">Based on</span>
							<span className="text-xs font-semibold text-gray-900">
								{totalAdjustments} adjustment{totalAdjustments !== 1 ? "s" : ""}
							</span>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Quick Summary */}
			<Card>
				<CardContent className="pt-3 pb-3 px-4">
					<div className="space-y-1.5">
						<div className="flex items-center justify-between">
							<span className="text-xs text-gray-600">Adjustments</span>
							<span className="text-xs font-semibold text-gray-900">
								{totalAdjustments}
							</span>
						</div>
						<div className="flex items-center justify-between">
							<span className="text-xs text-gray-600">Net Impact</span>
							<span
								className={`text-xs font-semibold ${netImpact >= 0 ? "[color:var(--accent-success-500)]" : "text-amber-600"}`}
							>
								{netImpact >= 0 ? "+" : ""}
								{formatCurrency(monthlyImpact * 12)}/year
							</span>
						</div>
						<div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
							<span className="text-xs text-gray-600">Monthly</span>
							<span
								className={`text-xs font-semibold ${netImpact >= 0 ? "[color:var(--accent-success-500)]" : "text-amber-600"}`}
							>
								{netImpact >= 0 ? "+" : ""}
								{formatCurrency(monthlyImpact)}/mo
							</span>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
};
